import React, { createContext, Suspense, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { HashRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './components/AuthContext';
import { HouseholdProvider } from './components/HouseholdContext';
import Layout from './components/Layout';
import { ProfileProvider } from './components/ProfileContext';
import ErrorBoundary from './components/ErrorBoundary';
import RequireAuth from './components/RequireAuth';
import { logger } from './utils/logger';
import { FREE_DAILY_SCAN_LIMIT, FREE_DAILY_RECIPE_LIMIT, FREE_DAILY_MANUAL_LIMIT, UNLIMITED } from './config/businessRules';
import { fetchSubscriptionStatus, SubscriptionStatusResponse } from './services/subscriptionApi';

// Lazy-loaded page components for code splitting
const AppAdmin = React.lazy(() => import('./pages/AppAdmin'));
const Beverages = React.lazy(() => import('./pages/Beverages'));
const CaregiverMode = React.lazy(() => import('./pages/CaregiverMode').then(m => ({ default: m.CaregiverMode })));
const CoachingPrograms = React.lazy(() => import('./pages/CoachingPrograms'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Education = React.lazy(() => import('./pages/Education'));
const FridgePantry = React.lazy(() => import('./pages/FridgePantry'));
const Groceries = React.lazy(() => import('./pages/Groceries'));
const HouseholdSettings = React.lazy(() => import('./pages/HouseholdSettings'));
const LearnMore = React.lazy(() => import('./pages/LearnMore'));
const Login = React.lazy(() => import('./pages/Login'));
const MealPlanner = React.lazy(() => import('./pages/MealPlanner'));
const Onboarding = React.lazy(() => import('./pages/Onboarding'));
const ProfileSettings = React.lazy(() => import('./pages/ProfileSettings'));
const Recipes = React.lazy(() => import('./pages/Recipes'));
const Register = React.lazy(() => import('./pages/Register'));
const Splash = React.lazy(() => import('./pages/Splash'));
const Upgrades = React.lazy(() => import('./pages/Upgrades'));
import { canGenerateRecipe, canScanPantry, getDailyManualCount, getDailyRecipeCount, getDailyScanCount, getTrialInfo, getTrialStatus } from './services/trialService';
import { TrialInfo, TrialStatus } from './types';
import { useAuth } from './components/AuthContext';

// Subscription Context for centralized auth/subscription state
interface SubscriptionState {
  trialStatus: TrialStatus;
  trialInfo: TrialInfo;
  isPremium: boolean;
  dailyScanCount: number;
  dailyRecipeCount: number;
  dailyManualCount: number;
  remainingScans: number;
  remainingRecipes: number;
  remainingManualIngredients: number;
  canScanPantry: () => boolean;
  canGenerateRecipe: () => boolean;
  canEditMealPlan: boolean;
  getEducationAccess: () => 'limited' | 'full';
  refreshSubscription: () => void;
}

const SubscriptionContext = createContext<SubscriptionState | null>(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [trialStatus, setTrialStatus] = useState<TrialStatus>('active');
  const [trialInfo, setTrialInfo] = useState<TrialInfo>({ status: 'active', startDate: null, daysRemaining: 7 });
  const [dailyScanCount, setDailyScanCount] = useState<number>(0);
  const [dailyRecipeCount, setDailyRecipeCount] = useState<number>(0);
  const [dailyManualCount, setDailyManualCount] = useState<number>(0);
  const [isPremiumFromServer, setIsPremiumFromServer] = useState<boolean>(false);
  const [serverLoaded, setServerLoaded] = useState(false);

  // Load subscription from server (primary) or localStorage (fallback)
  const loadSubscription = useCallback(async () => {
    if (isAuthenticated) {
      try {
        const status = await fetchSubscriptionStatus();
        setIsPremiumFromServer(status.isPremium);
        setDailyScanCount(status.usage.scanCount);
        setDailyRecipeCount(status.usage.recipeCount);
        setDailyManualCount(status.usage.manualCount);

        // Map server tier to TrialStatus for UI compatibility
        if (status.isPremium && !status.trialActive) {
          setTrialStatus('premium');
          setTrialInfo({ status: 'premium', startDate: null, daysRemaining: -1 });
        } else if (status.trialActive) {
          setTrialStatus('active');
          setTrialInfo({
            status: 'active',
            startDate: status.trialStartDate,
            daysRemaining: status.trialDaysRemaining,
          });
        } else if (status.tier === 'expired_trial') {
          setTrialStatus('expired');
          setTrialInfo({ status: 'expired', startDate: status.trialStartDate, daysRemaining: 0 });
        } else {
          setTrialStatus('active');
          setTrialInfo({ status: 'active', startDate: null, daysRemaining: 7 });
        }

        setServerLoaded(true);
        return;
      } catch (error) {
        logger.warn('Failed to load subscription from server, falling back to localStorage:', error);
      }
    }

    // Fallback: load from localStorage (trialService)
    setTrialStatus(getTrialStatus());
    setTrialInfo(getTrialInfo());
    setDailyScanCount(getDailyScanCount());
    setDailyRecipeCount(getDailyRecipeCount());
    setDailyManualCount(getDailyManualCount());
    setIsPremiumFromServer(false);
    setServerLoaded(false);
  }, [isAuthenticated]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  const refreshSubscription = useCallback(() => {
    loadSubscription();
  }, [loadSubscription]);

  // Server is source of truth for premium status.
  // For authenticated users, never trust localStorage — default to non-premium on server failure.
  // Only fall back to localStorage for unauthenticated/guest users.
  const isPremium = serverLoaded
    ? isPremiumFromServer
    : isAuthenticated
      ? false
      : getTrialStatus() === 'premium';

  const subscriptionState: SubscriptionState = useMemo(() => ({
    trialStatus,
    trialInfo,
    isPremium,
    dailyScanCount,
    dailyRecipeCount,
    dailyManualCount,
    remainingScans: isPremium ? UNLIMITED : Math.max(0, FREE_DAILY_SCAN_LIMIT - dailyScanCount),
    remainingRecipes: isPremium ? UNLIMITED : Math.max(0, FREE_DAILY_RECIPE_LIMIT - dailyRecipeCount),
    remainingManualIngredients: isPremium ? UNLIMITED : Math.max(0, FREE_DAILY_MANUAL_LIMIT - dailyManualCount),
    canScanPantry: () => isPremium || (serverLoaded ? dailyScanCount < FREE_DAILY_SCAN_LIMIT : canScanPantry()),
    canGenerateRecipe: () => isPremium || (serverLoaded ? dailyRecipeCount < FREE_DAILY_RECIPE_LIMIT : canGenerateRecipe()),
    canEditMealPlan: isPremium,
    getEducationAccess: () => isPremium ? 'full' : 'limited',
    refreshSubscription
  }), [trialStatus, trialInfo, isPremium, dailyScanCount, dailyRecipeCount, dailyManualCount, serverLoaded, refreshSubscription]);

  return (
    <SubscriptionContext.Provider value={subscriptionState}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// Loading fallback for lazy-loaded pages
const PageLoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-sage-50">
    <div className="text-center">
      <div className="w-10 h-10 border-4 border-sage-300 border-t-sage-600 rounded-full animate-spin mx-auto mb-4" />
      <p className="text-sage-600 text-sm font-medium">Loading...</p>
    </div>
  </div>
);

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ProfileProvider>
        <SubscriptionProvider>
          <HouseholdProvider>
            <HashRouter>
              <Suspense fallback={<PageLoadingFallback />}>
              <Routes>
                {/* Public routes (no layout, no guard) */}
                <Route path="/" element={<ErrorBoundary><Splash /></ErrorBoundary>} />
                <Route path="/login" element={<ErrorBoundary><Login /></ErrorBoundary>} />
                <Route path="/register" element={<ErrorBoundary><Register /></ErrorBoundary>} />
                <Route path="/learn-more" element={<ErrorBoundary><LearnMore /></ErrorBoundary>} />
                <Route path="/onboarding" element={<ErrorBoundary><Onboarding /></ErrorBoundary>} />

                {/* Protected routes with auth guard and layout (some allow guest browsing) */}
                <Route path="/app" element={<ErrorBoundary><Layout /></ErrorBoundary>}>
                  <Route index element={<ErrorBoundary><RequireAuth allowGuest><Dashboard /></RequireAuth></ErrorBoundary>} />
                  <Route path="coaching" element={<ErrorBoundary><RequireAuth><CoachingPrograms /></RequireAuth></ErrorBoundary>} />
                  <Route path="recipes" element={<ErrorBoundary><RequireAuth><Recipes /></RequireAuth></ErrorBoundary>} />
                  <Route path="meal-plan" element={<ErrorBoundary><RequireAuth><MealPlanner /></RequireAuth></ErrorBoundary>} />
                  <Route path="groceries" element={<ErrorBoundary><RequireAuth><Groceries /></RequireAuth></ErrorBoundary>} />
                  <Route path="pantry" element={<ErrorBoundary><RequireAuth><FridgePantry /></RequireAuth></ErrorBoundary>} />
                  <Route path="beverages" element={<ErrorBoundary><RequireAuth><Beverages /></RequireAuth></ErrorBoundary>} />
                  <Route path="education" element={<ErrorBoundary><RequireAuth allowGuest><Education /></RequireAuth></ErrorBoundary>} />
                  <Route path="caregiver" element={<ErrorBoundary><RequireAuth><CaregiverMode /></RequireAuth></ErrorBoundary>} />
                  <Route path="upgrades" element={<ErrorBoundary><RequireAuth><Upgrades /></RequireAuth></ErrorBoundary>} />
                  <Route path="profile-settings" element={<ErrorBoundary><RequireAuth><ProfileSettings /></RequireAuth></ErrorBoundary>} />
                  <Route path="household-settings" element={<ErrorBoundary><RequireAuth><HouseholdSettings /></RequireAuth></ErrorBoundary>} />
                  <Route path="admin" element={<ErrorBoundary><RequireAuth><AppAdmin /></RequireAuth></ErrorBoundary>} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </Suspense>
            </HashRouter>
          </HouseholdProvider>
        </SubscriptionProvider>
      </ProfileProvider>
    </AuthProvider>
  );
};

export default App;